/**
 * MSAL (Microsoft Authentication Library) configuration for Entra ID authentication.
 * All values are loaded from environment variables at build time via Vite.
 */

import { type Configuration, LogLevel, type RedirectRequest } from "@azure/msal-browser";

const clientId = import.meta.env.VITE_ENTRA_CLIENT_ID as string | undefined;
const tenantId = import.meta.env.VITE_ENTRA_TENANT_ID as string | undefined;
const redirectUri = import.meta.env.VITE_REDIRECT_URI as string | undefined;

if (!clientId) {
  throw new Error("VITE_ENTRA_CLIENT_ID environment variable is required");
}

if (!tenantId) {
  throw new Error("VITE_ENTRA_TENANT_ID environment variable is required");
}

/**
 * MSAL PublicClientApplication configuration.
 */
export const msalConfig: Configuration = {
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri: redirectUri ?? "http://localhost:5173",
    postLogoutRedirectUri: redirectUri ?? "http://localhost:5173",
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: "sessionStorage",
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (_level: LogLevel, message: string, containsPii: boolean) => {
        if (containsPii) {
          return;
        }
        switch (_level) {
          case LogLevel.Error:
            console.error(`[MSAL] ${message}`);
            break;
          case LogLevel.Warning:
            console.warn(`[MSAL] ${message}`);
            break;
          default:
            break;
        }
      },
      logLevel: LogLevel.Warning,
      piiLoggingEnabled: false,
    },
  },
};

/**
 * Scopes for the API access token.
 */
export const apiScopes: readonly string[] = [
  `api://${clientId}/access_as_user`,
];

/**
 * Login request configuration.
 */
export const loginRequest: RedirectRequest = {
  scopes: [...apiScopes],
};
